import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Ticket, ArrowLeft, Plus, Package, Check, Trash2,
  Clock, CheckCircle2, ShoppingCart, Loader2, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── Order-Item Row in Check-In Modal ──────────────────────────────────────────
const CheckInItemRow = ({ item, onChange }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-900 dark:text-white">{item.product_name}</p>
      <p className="text-xs text-gray-400">Bestellt: {item.ordered_packages} Pkt.</p>
    </div>
    <div className="flex items-center gap-2">
      <Label className="text-xs text-gray-500 whitespace-nowrap">Geliefert:</Label>
      <Input
        type="number"
        min={0}
        value={item.delivered_packages ?? item.ordered_packages}
        onChange={(e) => onChange(item.product_id, parseInt(e.target.value) || 0)}
        className="w-20 h-8 text-center text-sm"
        data-testid={`checkin-qty-${item.product_id}`}
      />
    </div>
  </div>
);

export const LottoRubellose = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bestellungen');

  // ── Data State ────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // ── Create Order Modal ────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [orderItems, setOrderItems] = useState([]); // [{product, qty}]
  const [orderNotes, setOrderNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // ── Check-In Modal ────────────────────────────────────────────────────────
  const [checkInOrder, setCheckInOrder] = useState(null);
  const [checkInItems, setCheckInItems] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);

  // ── Load Data ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes, countRes] = await Promise.all([
        axios.get(`${API}/api/lotto/orders`, { headers: authHeader() }),
        axios.get(`${API}/api/lotto/products`, { headers: authHeader() }),
        axios.get(`${API}/api/lotto/orders/pending-count`, { headers: authHeader() }),
      ]);
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
      setPendingCount(countRes.data?.pending_count || 0);
    } catch {
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Create Order Helpers ──────────────────────────────────────────────────
  const openCreateModal = () => {
    setOrderItems(products.map(p => ({ product: p, qty: 0 })));
    setOrderNotes('');
    setShowCreateModal(true);
  };

  const handleQtyChange = (productId, qty) => {
    setOrderItems(prev => prev.map(it =>
      it.product.id === productId ? { ...it, qty: Math.max(0, qty) } : it
    ));
  };

  const handleCreateOrder = async () => {
    const selected = orderItems.filter(it => it.qty > 0);
    if (!selected.length) {
      toast.error('Bitte mindestens ein Produkt mit Menge > 0 auswählen');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/lotto/orders`, {
        items: selected.map(it => ({
          product_id: it.product.id,
          product_name: it.product.name,
          ordered_packages: it.qty,
        })),
        notes: orderNotes,
      }, { headers: authHeader() });
      toast.success('Bestellung erfasst', { description: `${selected.length} Produkt(e) bestellt` });
      setShowCreateModal(false);
      load();
    } catch {
      toast.error('Fehler beim Erstellen der Bestellung');
    } finally {
      setCreating(false);
    }
  };

  // ── Check-In Helpers ──────────────────────────────────────────────────────
  const openCheckIn = (order) => {
    setCheckInOrder(order);
    setCheckInItems(order.items.map(it => ({ ...it })));
  };

  const handleCheckInQtyChange = (productId, qty) => {
    setCheckInItems(prev => prev.map(it =>
      it.product_id === productId ? { ...it, delivered_packages: qty } : it
    ));
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      await axios.post(`${API}/api/lotto/orders/${checkInOrder.id}/check-in`, {
        items: checkInItems.map(it => ({
          product_id: it.product_id,
          delivered_packages: it.delivered_packages ?? it.ordered_packages,
        })),
      }, { headers: authHeader() });
      toast.success('Wareneingang eingebucht', { description: 'Bestand wurde aktualisiert' });
      setCheckInOrder(null);
      load();
    } catch {
      toast.error('Fehler beim Einbuchen');
    } finally {
      setCheckingIn(false);
    }
  };

  // ── Delete Order ──────────────────────────────────────────────────────────
  const handleDelete = async (orderId) => {
    if (!window.confirm('Bestellung wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/api/lotto/orders/${orderId}`, { headers: authHeader() });
      toast.success('Bestellung gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────
  const pending = orders.filter(o => o.status === 'pending');
  const delivered = orders.filter(o => o.status === 'delivered');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/lotto')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Rubellose</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Bestellungen erfassen & Wareneingang buchen</p>
          </div>
          {activeTab === 'bestellungen' && (
            <Button onClick={openCreateModal} className="flex items-center gap-2" data-testid="create-order-btn">
              <Plus className="h-4 w-4" /> Bestellung erfassen
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => navigate('/admin/lotto/produktverwaltung')}
              className="border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
            >
              Produktverwaltung
            </button>
            <button
              onClick={() => navigate('/admin/lotto/rubellose')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'bestellungen'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300'
              }`}
            >
              Bestellungen
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-bold bg-amber-400 text-white" data-testid="pending-badge">
                  {pendingCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
        ) : (
          <div className="space-y-6">
            {/* Pending Orders */}
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Ausstehend
                {pending.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400">
                    {pending.length}
                  </span>
                )}
              </h2>

              {pending.length === 0 ? (
                <Card>
                  <CardContent className="py-10 text-center text-gray-400 dark:text-gray-500">
                    <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Keine ausstehenden Bestellungen</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pending.map(order => (
                    <Card key={order.id} className="border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10" data-testid="order-pending-card">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-100">
                                <Clock className="h-3 w-3" /> Bestellt / Unterwegs
                              </span>
                              <span className="text-xs text-gray-400">{fmt(order.ordered_at)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">von {order.ordered_by_name}</p>
                            {order.notes && <p className="text-xs text-gray-400 mt-0.5 italic">„{order.notes}"</p>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              onClick={() => openCheckIn(order)}
                              className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`checkin-btn-${order.id}`}
                            >
                              <Check className="h-3.5 w-3.5" /> Eingegangen
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                              className="h-8 w-8 p-0 text-gray-400"
                            >
                              {expandedOrder === order.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(order.id)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                              data-testid={`delete-order-btn-${order.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Expanded items */}
                        {expandedOrder === order.id && (
                          <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800 space-y-1">
                            {order.items.map(it => (
                              <div key={it.product_id} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                                  <Ticket className="h-3.5 w-3.5 text-amber-500" /> {it.product_name}
                                </span>
                                <span className="font-medium text-gray-900 dark:text-white">{it.ordered_packages} Pkt.</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Delivered Orders */}
            {delivered.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Eingebucht
                </h2>
                <div className="space-y-2">
                  {delivered.slice(0, 10).map(order => (
                    <Card key={order.id} className="border-green-200 dark:border-green-900 bg-green-50/30 dark:bg-green-900/10" data-testid="order-delivered-card">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-100">
                                <CheckCircle2 className="h-3 w-3" /> Geliefert
                              </span>
                              <span className="text-xs text-gray-400">{fmt(order.delivered_at)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">Eingebucht von {order.delivered_by_name}</p>
                          </div>
                          <button
                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {expandedOrder === order.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                        {expandedOrder === order.id && (
                          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-900 space-y-1">
                            {order.items.map(it => (
                              <div key={it.product_id} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                                  <Ticket className="h-3.5 w-3.5 text-green-500" /> {it.product_name}
                                </span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {it.delivered_packages ?? it.ordered_packages} Pkt.
                                  {it.delivered_packages !== it.ordered_packages && it.delivered_packages !== null && (
                                    <span className="ml-1 text-xs text-amber-500">(bestellt: {it.ordered_packages})</span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Create Order Modal ────────────────────────────────────────────── */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-blue-500" />
              Lotto-Bestellung erfassen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {products.length === 0 ? (
              <div className="flex items-center gap-2 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-700 dark:text-amber-300 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                Keine Produkte vorhanden. Bitte zuerst Produkte in der Produktverwaltung anlegen.
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pakete pro Produkt eingeben (0 = nicht bestellt):</p>
                <div className="space-y-2">
                  {orderItems.map(({ product, qty }) => (
                    <div key={product.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Ticket className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{product.name}</p>
                          <p className="text-xs text-gray-400">Bestand: {product.stock} Stk.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleQtyChange(product.id, qty - 1)}
                          className="h-7 w-7 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >−</button>
                        <Input
                          type="number"
                          min={0}
                          value={qty}
                          onChange={(e) => handleQtyChange(product.id, parseInt(e.target.value) || 0)}
                          className={`w-16 h-7 text-center text-sm ${qty > 0 ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          data-testid={`order-qty-${product.id}`}
                        />
                        <button
                          onClick={() => handleQtyChange(product.id, qty + 1)}
                          className="h-7 w-7 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >+</button>
                        <Label className="text-xs text-gray-400">Pkt.</Label>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <Label htmlFor="order-notes" className="text-sm">Notiz (optional)</Label>
                  <Input
                    id="order-notes"
                    placeholder="z.B. dringend, Terminal-Nr. ..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="mt-1"
                    data-testid="order-notes-input"
                  />
                </div>
                {orderItems.filter(it => it.qty > 0).length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Zusammenfassung:</p>
                    {orderItems.filter(it => it.qty > 0).map(it => (
                      <p key={it.product.id} className="text-xs text-blue-600 dark:text-blue-400">
                        · {it.product.name}: {it.qty} Pkt.
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Abbrechen</Button>
            <Button
              onClick={handleCreateOrder}
              disabled={creating || orderItems.filter(it => it.qty > 0).length === 0}
              data-testid="submit-order-btn"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
              Bestellung erfassen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Check-In Modal ─────────────────────────────────────────────────── */}
      <Dialog open={!!checkInOrder} onOpenChange={(o) => !o && setCheckInOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Wareneingang bestätigen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Bitte gelieferte Paketanzahl prüfen und anpassen, falls nötig:
            </p>
            {checkInItems.map(it => (
              <CheckInItemRow
                key={it.product_id}
                item={it}
                onChange={handleCheckInQtyChange}
              />
            ))}
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs text-green-700 dark:text-green-300">
              Der Bestand wird um die eingetragene Menge erhöht (unabhängig vom aktuellen Bestand).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckInOrder(null)}>Abbrechen</Button>
            <Button
              onClick={handleCheckIn}
              disabled={checkingIn}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-checkin-btn"
            >
              {checkingIn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Einbuchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
